import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { BY_IDS_LIFETIME, normalizePresentIds, requestInChunks } from '@/utils/ids';

import { getChannels } from '../api/get-channels';
import { channelsQueryKeys } from '../api/query-keys';
import { ChannelType } from '../types';

/**
 * Loads the given channels by id. Duplicates and nullish ids are dropped, and
 * the ids are sorted, so the same set in another order reuses the cached
 * answer. Nothing is requested for an empty set: the API reads an empty
 * `id__in` as "no filter" and returns every channel.
 */
export function useChannelsByIds(ids: readonly (string | null | undefined)[]) {
  const idsKey = normalizePresentIds(ids).join(',');
  const idIn = useMemo(() => (idsKey ? idsKey.split(',') : []), [idsKey]);
  const hasIds = idIn.length > 0;

  // A key of its own rather than `useChannels`: other callers cache under
  // `list({ idIn })`, and one passing an empty list would cache the whole
  // catalogue under `idIn: []`, which the placeholder would carry in as if
  // those channels had been fetched here.
  const query = useQuery({
    queryKey: channelsQueryKeys.byIds(idIn),
    queryFn: async () => {
      const pages = await requestInChunks(idIn, chunkIds => getChannels({ idIn: chunkIds }));

      return {
        data: pages.flatMap(page => page.data),
        total: pages.reduce((n, p) => n + p.total, 0),
      };
    },
    enabled: hasIds,
    // `gcTime` matches it: the app's five-minute default would drop the entry long before
    // it goes stale.
    staleTime: BY_IDS_LIFETIME,
    gcTime: BY_IDS_LIFETIME,
    // On a new set of ids, the channels already known stay in the map while
    // the rest load, so a row whose parent was on the previous page shows it
    // at once. TanStack carries it across an empty set too, which the
    // Parrilla goes through on every page change.
    placeholderData: keepPreviousData,
  });

  const channelsById = useMemo(
    () => new Map<string, ChannelType>(query.data?.data.map(channel => [channel.id, channel])),
    [query.data]
  );

  return {
    channelsById,
    // Pending until the answer for these ids arrives. Placeholder data counts
    // as pending, so a channel missing from it waits instead of falling back.
    // If the request fails the placeholder is dropped and every row falls back
    // to the default logo. A disabled query reports `isPending` too, which
    // would leave every row waiting for parents nobody asked for.
    isPending: hasIds && (query.isPending || query.isPlaceholderData),
  };
}
